/**
 * Queries of the calendar page (1.O-3 deliverable 3). Keys sit under `['calendar', …]`. Loading
 * the page sends one `GET`; the import and the diff are asked only after the GM chose a file, and
 * nothing here writes.
 */
import {
  type CalendarDefinition,
  type CalendarDiffV1,
  type CalendarImportV1,
  type CalendarRemap,
  type CalendarV1,
  calendarDiff,
  calendarGet,
  calendarImportFoundry,
} from '@codex/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../auth/client.js';
import { draftKey } from './calendar-model.js';

export const calendarKey = ['calendar', 'current'] as const;
export const DIFF_DEBOUNCE_MS = 400;

/** The stored calendar, or null while there is none (`calendar.get` answers 200 null, DECISIONS 567). */
export function useCalendar() {
  return useQuery({
    queryKey: calendarKey,
    queryFn: (): Promise<CalendarV1 | null> => api(calendarGet),
    retry: false,
  });
}

/** What the file said, read by the server; asked again when the season reading changes. */
export function useImport(
  file: { readonly id: number; readonly json: Record<string, unknown> } | null,
  seasonDayBase: 0 | 1,
  keepPrevious = false,
) {
  return useQuery({
    queryKey: ['calendar', 'import', file?.id ?? 0, seasonDayBase],
    queryFn: () =>
      api(calendarImportFoundry, { body: { calendar: file?.json ?? {}, seasonDayBase } }),
    enabled: file !== null,
    // A new season reading keeps the last answer of the same file on screen (and the draft, the
    // diff and the leave guard with it) until its own answer arrives.
    ...(keepPrevious
      ? {
          placeholderData: (previous: CalendarImportV1 | undefined, previousQuery: unknown) =>
            (previousQuery as { queryKey?: readonly unknown[] } | undefined)?.queryKey?.[2] ===
            file?.id
              ? previous
              : undefined,
        }
      : {}),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
  });
}

export interface DiffState {
  /** The latest answer, with the `draftKey` it was asked for; kept while a newer one is asked. */
  readonly answer: { readonly key: string; readonly diff: CalendarDiffV1 } | null;
  readonly failed: boolean;
  readonly retry: () => void;
}

/**
 * `calendar.diff` for a draft: sent 400 ms after the last change, one request in flight, an
 * answer kept only when it is for the draft now on screen. A new `version` of the stored
 * calendar asks again.
 */
export function useDiff(
  definition: CalendarDefinition | null,
  remap: CalendarRemap,
  version: number | null,
): DiffState {
  const key = definition === null ? null : draftKey(definition, remap);
  const [attempt, setAttempt] = useState(0);
  const requestKey = key === null || version === null ? null : `${version}|${attempt}|${key}`;
  const [answer, setAnswer] = useState<DiffState['answer']>(null);
  const [failed, setFailed] = useState(false);
  const latest = useRef({ definition, remap, key, requestKey });
  latest.current = { definition, remap, key, requestKey };
  const inFlight = useRef(false);
  const queued = useRef(false);

  const run = useCallback(async () => {
    if (inFlight.current) {
      queued.current = true;
      return;
    }
    inFlight.current = true;
    try {
      do {
        queued.current = false;
        const sent = latest.current;
        if (sent.definition === null || sent.requestKey === null) break;
        try {
          const diff = await api(calendarDiff, {
            body: { definition: sent.definition, remap: sent.remap },
          });
          if (latest.current.requestKey === sent.requestKey && sent.key !== null) {
            setAnswer({ key: sent.key, diff });
            setFailed(false);
          }
        } catch {
          if (latest.current.requestKey === sent.requestKey) setFailed(true);
        }
      } while (queued.current);
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (requestKey === null) {
      setAnswer(null);
      setFailed(false);
      return;
    }
    const timer = setTimeout(() => void run(), DIFF_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [requestKey, run]);

  return { answer, failed, retry: () => setAttempt((n) => n + 1) };
}
